import { z } from 'zod';
import { MetaSchema } from './create-chat.js';
import { UsagePeriodSchema } from './usage.js';

/**
 * Budget settings contracts (ADR-0078 §2, D17-01) — the tenant's own choice of
 * alert thresholds and overage mode, plus this period's status. The Owner
 * writes; Owner and Admin read. `data` is strict (the BFF authors it); the
 * envelope is loose (ADR-0024 §11).
 */

/** What happens once the included credits are used up (ADR-0078 §4). */
export const BudgetModeSchema = z.enum(['auto-continue', 'approval', 'hard-stop']);
export type BudgetMode = z.infer<typeof BudgetModeSchema>;

/** Percent of the included credits at which an alert fires (ADR-0078 §3). */
const ThresholdSchema = z.number().int().min(1).max(100);

/** Body of `PUT /api/v1/workstreams/budget`. Thresholds are stored sorted. */
export const BudgetSettingsRequestSchema = z.strictObject({
  thresholds: z
    .array(ThresholdSchema)
    .min(1)
    .max(5)
    .refine((t) => new Set(t).size === t.length, 'thresholds must be distinct'),
  mode: BudgetModeSchema,
});
export type BudgetSettingsRequest = z.infer<typeof BudgetSettingsRequestSchema>;

export const BudgetSettingsSchema = z.strictObject({
  thresholds: z.array(ThresholdSchema),
  mode: BudgetModeSchema,
  /** Absent until an Owner first saves — the defaults (70/85/100, auto-continue) apply. */
  updatedAt: z.string().optional(),
  /** Auth0 `sub` of the Owner who last saved. */
  updatedBy: z.string().optional(),
});
export type BudgetSettings = z.infer<typeof BudgetSettingsSchema>;

/** One threshold reached this period (an `ALERT#{period}#{threshold}` item, D17-02). */
export const BudgetAlertSchema = z.strictObject({
  threshold: ThresholdSchema,
  /** RFC-3339 UTC — when it was first reached. */
  at: z.string(),
});
export type BudgetAlert = z.infer<typeof BudgetAlertSchema>;

/** `data` of `GET /api/v1/workstreams/budget`. */
export const BudgetDataSchema = z.strictObject({
  settings: BudgetSettingsSchema,
  status: z.strictObject({
    /** Current UTC billing period, server-determined. */
    period: UsagePeriodSchema,
    /** Credits used this period. Absent — never `"0"` — while any turn is unpriced. */
    consumedCredits: z.string().optional(),
    /** The plan's allowance. Absent when the tenant has no plan (no enforcement). */
    includedCredits: z.string().optional(),
    /** Credits the Owner added to this period's cap (ADR-0078 §4, D17-07). Absent when none. */
    extensionCredits: z.string().optional(),
    /** Thresholds reached this period, lowest first ("active alerts"). */
    alerts: z.array(BudgetAlertSchema),
  }),
});
export type BudgetData = z.infer<typeof BudgetDataSchema>;

export const BudgetResponseSchema = z.looseObject({
  data: BudgetDataSchema,
  meta: MetaSchema,
});
export type BudgetResponse = z.infer<typeof BudgetResponseSchema>;

/**
 * Body of `POST /api/v1/workstreams/budget/extension` (ADR-0078 §4, D17-07) — the Owner
 * raises this period's cap. A credit is shown as A$1 (§1), so `amountAud` is the credit
 * count added. Positive, at most two decimals, and bounded so a typo can't lift a cap
 * by millions.
 */
export const BudgetExtensionRequestSchema = z.strictObject({
  amountAud: z
    .string()
    .regex(/^\d{1,6}(\.\d{1,2})?$/, 'must be a plain amount like "100" or "250.50"')
    .refine((v) => Number(v) > 0, 'must be more than zero'),
});
export type BudgetExtensionRequest = z.infer<typeof BudgetExtensionRequestSchema>;

/** `data` of the extension POST — this period's extension total after the add. */
export const BudgetExtensionDataSchema = z.strictObject({
  period: UsagePeriodSchema,
  extensionCredits: z.string(),
});
export type BudgetExtensionData = z.infer<typeof BudgetExtensionDataSchema>;

export const BudgetExtensionResponseSchema = z.looseObject({
  data: BudgetExtensionDataSchema,
  meta: MetaSchema,
});

/** `PUT /api/v1/workstreams/budget` — `data` is the saved settings only. */
export const BudgetPutResponseSchema = z.looseObject({
  data: BudgetSettingsSchema,
  meta: MetaSchema,
});
